import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { get, isEmpty, pull, find, isString, take, filter, isArray, sortBy, head } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { findDocuments, findPatients } from './_api';
import { downloadPdfDocument, getPdfDocument } from '../Patient/_api';
import { getClinicalPortalSetting } from 'modules/Administration/ClinicalPortal/_api';
import { IGridLayoutParams, IPatientDocument } from '../Patient/_types';
import { DocumentDetailGrids } from './DocumentDetailGrids';
import { useActions } from 'utils/hooks/useActions';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useLanguage } from 'utils/hooks/useLanguage';
import { apiDataToDownloadFile, printOrDownloadBase64String } from 'utils/hooks/useApp';
import useAlerts from 'components/Alerts/useAlerts';
import Header from 'components/Header/Header';
import { hookDownloadFromEldax } from '../Patient/hooks';

const sxDisplayPrintNone = { '@media print': { display: 'none' } };

export const ClinicalPortalDocumentDetailFromTagsFhir: React.FC = () => {
  const { t } = useTranslation('ClinicalPortal');
  const { toggleLoader } = useAppGlobals();
  const { currentLocale } = useLanguage();
  let [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { addErrorAlert } = useAlerts();
  const { clinicalPortalSettingFromRedux, selectedDocuments } = useAppInfo();
  const { storeClinicalPortalSetting } = useActions();

  const from = searchParams.get('from');
  const backToPathname = searchParams.get('pathname');
  const linkBack = backToPathname
    ? `${backToPathname}?action=back${from ? `&from=${from}` : ''}`
    : '/';

  const [entity, setEntity] = useState<any>(null);
  const [gridLayout, setGridLayout] = useState<string>('');
  const [patientDocuments, setPatientDocuments] = useState<IPatientDocument[]>([]);
  const [documentUids, setDocumentUids] = useState<string[]>([]);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [standardizedPrint, setStandardizedPrint] = useState<boolean>(false);
  const [isDownloadingFromEldax, setIsDownloadingFromEldax] = useState<boolean>(false);

  const loadPatientAndDocuments = async (masterPatientID: string) => {
    const findPatientArray = await findPatients({ masterPatientID });
    const patient = isArray(findPatientArray) && find(findPatientArray, { masterPatientID });
    if (!patient) {
      addErrorAlert(t('getDataError'));
      navigate(linkBack);
      return;
    }
    setEntity(patient);

    let documents: IPatientDocument[] = [];

    let clinicalPortalSetting = clinicalPortalSettingFromRedux;
    if (!clinicalPortalSetting) {
      clinicalPortalSetting = await getClinicalPortalSetting();
      storeClinicalPortalSetting(clinicalPortalSetting);
    }
    let activeTab = '';
    let bookmarks: any[] = [];
    if (clinicalPortalSetting) {
      bookmarks = sortBy(filter(get(clinicalPortalSetting, 'bookmarks', []), { enabled: true }), [
        'position',
      ]);
    }
    const first = head(bookmarks);
    if (first !== undefined) {
      activeTab = get(first, 'id', '');
    }
    const documentArray: IPatientDocument[] = await findDocuments(masterPatientID, activeTab);
    if (!documentArray) {
      addErrorAlert(t('noFindPatient'));
      navigate(linkBack);
    }

    documents = documentArray.map((item, index: number) => ({
      ...item,
      index,
      uuidex: `${item.uuid}_${index}`,
      cannotBySelected: get(item, 'typeOfDocument.name', '') === 'DICOM',
      showDocumentIcon: 'Search',
      typeOfDocumentName: get(item, 'typeOfDocument.translateJson')
        ? get(
            JSON.parse(get(item, 'typeOfDocument.translateJson')),
            currentLocale,
            get(item, 'typeOfDocument.name'),
          )
        : '?',
      patientEntity: patient,
      patient,
    }));

    setPatientDocuments([...documents]);
  };

  const loadData = async () => {
    toggleLoader();
    const selectedDocumentsCount = selectedDocuments.length;
    setGridLayout(
      `${Math.ceil(selectedDocumentsCount / 4)}x${
        selectedDocumentsCount > 4 ? 4 : selectedDocumentsCount
      }`,
    );
    const uuid: string = get(selectedDocuments[0], 'uuid', '') || '';
    const id: string = get(selectedDocuments[0], 'patient.masterPatientID', '') || '';

    if (uuid) {
      setDocumentUids([uuid]);
    }
    if (id) {
      await loadPatientAndDocuments(id);
    }
    toggleLoader(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDocumentForPrint = async (
    masterPatientID: string,
    documentUiid: string,
    typeOfDocument: string,
    isPrinting: boolean,
    isExporting: boolean,
    standardizedPrint: boolean,
  ) => {
    if (!isEmpty(masterPatientID) && !isEmpty(documentUiid) && !isEmpty(typeOfDocument)) {
      const documentPrintInfoResponse = await getPdfDocument(
        masterPatientID,
        documentUiid,
        typeOfDocument,
        standardizedPrint,
      );
      if (documentPrintInfoResponse) {
        return documentPrintInfoResponse;
      } else {
        if (isPrinting) {
          addErrorAlert(t('printDocumentError'));
        }
        if (isExporting) {
          addErrorAlert(t('exportDocumentError'));
        }
      }
    }
    return undefined;
  };

  const printOrExport = async () => {
    toggleLoader();
    for await (const uuid of selectedDocuments) {
      const patientdoc = find(patientDocuments, { uuid });
      const masterPatientID = get(entity, 'masterPatientID', '');
      const fileName = masterPatientID + '.pdf';
      if (isPrinting) {
        const base64PdfString = await loadDocumentForPrint(
          masterPatientID,
          uuid,
          get(patientdoc, 'typeOfDocument.name', ''),
          isPrinting,
          isExporting,
          standardizedPrint,
        );
        const contentType = 'application/pdf';
        printOrDownloadBase64String(base64PdfString, contentType, fileName);
        setStandardizedPrint(false);
        setIsPrinting(false);
      } else if (isExporting) {
        const base64PdfString = await downloadPdfDocument(
          masterPatientID,
          uuid,
          get(patientdoc, 'typeOfDocument.name', ''),
          standardizedPrint,
        );
        apiDataToDownloadFile(base64PdfString, fileName);
        setStandardizedPrint(false);
        setIsExporting(false);
      }
    }
    toggleLoader(false);
  };
  useEffect(() => {
    if (isPrinting || isExporting) {
      printOrExport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrinting, isExporting]);

  const downloadFromEldaxFunction = async () => {
    toggleLoader();
    for await (const uuid of selectedDocuments) {
      const patientdoc = find(patientDocuments, { uuid });
      const masterPatientID = get(entity, 'masterPatientID', null);
      hookDownloadFromEldax(
        masterPatientID,
        patientdoc,
        uuid,
        t,
        addErrorAlert,
        setIsDownloadingFromEldax,
      );
    }
    toggleLoader(false);
  };
  useEffect(() => {
    if (isDownloadingFromEldax) {
      downloadFromEldaxFunction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDownloadingFromEldax]);

  const unselectDocumentUuid = (uuid: string) => {
    const reducedDocumentUids = [...documentUids];
    pull(reducedDocumentUids, uuid);

    setDocumentUids(reducedDocumentUids);
  };
  const selectDocumentUuid = (uuid: string) => {
    const updatedDocumentUids = [...documentUids];
    updatedDocumentUids.push(uuid);

    setDocumentUids(updatedDocumentUids);
  };

  const replaceDocumentAtIndex = (uuid: string, index: number) => {
    const currentUids = [...documentUids];
    currentUids[index] = uuid;
    setDocumentUids(currentUids);
  };

  const [gridLayoutParams, setGridLayoutParams] = useState<IGridLayoutParams>({
    x: 1,
    y: 1,
    totalAvailableFields: 1,
    lastDocumentPositionIndex: 1,
  });

  useEffect(() => {
    if (isString(gridLayout)) {
      const dimesions = gridLayout.split('x');
      if (dimesions.length === 2) {
        const x = Number(dimesions[0]);
        const y = Number(dimesions[1]);
        const max = x * y;
        const newUids = take(documentUids, max);
        setDocumentUids(newUids);
        setGridLayoutParams({
          x,
          y,
          totalAvailableFields: x * y,
          lastDocumentPositionIndex: documentUids.length - 1,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLayout, documentUids.length]);

  return (
    <>
      <Typography component="div" sx={sxDisplayPrintNone}>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Header title={t('documentsDetail')} backUrl={linkBack} />
        </Box>
      </Typography>

      {entity && documentUids && !isEmpty(patientDocuments) && patientDocuments.length > 0 && (
        <Box sx={sxDisplayPrintNone}>
          <DocumentDetailGrids
            gridLayout={gridLayout}
            documentUids={documentUids}
            selectDocumentUuid={selectDocumentUuid}
            unselectDocumentUuid={unselectDocumentUuid}
            patientDocuments={patientDocuments}
            onlyAddTag={true}
            setIsPrinting={setIsPrinting}
            setIsExporting={setIsExporting}
            replaceDocumentAtIndex={replaceDocumentAtIndex}
            setStandardizedPrint={setStandardizedPrint}
            requestedDocuments={[]}
            gridLayoutParams={gridLayoutParams}
            setIsDownloadingFromEldax={setIsDownloadingFromEldax}
          />
        </Box>
      )}
    </>
  );
};
